import { EMPTY_STRING, Nullable, Optional } from '@rotomeca/utils';
import { TAG_BUTTON } from '../../../core/utils/constants/tags';
import { REG_XSS_SAFE } from '../../../core/utils/constants/regexp';
import { HTMLBnumIcon } from '../icon/bnum-icon';
import style from './bnum-button.less';
import { Scheduler } from '../../../core/utils/Scheduler';
import { Autobind, Define, Fire, SetAttr } from '../../../core/decorators';
import {
	ButtonVariation,
	IconPosition,
	HideTextOnLayoutSize,
	BnumButtonCreateOptions,
	IconChangedEvent,
	LoadingStateChangedEvent,
	IconPropChangedEvent,
	VariationChangedEvent,
	LinkedClickEvent,
	OnClickInitializer,
	OnIconChangeInitializer,
	OnIconPropChangeInitializer,
	OnLoadingStateChangeInitializer,
	OnVariationChangeInitializer,
	CLASS_WRAPPER,
	CLASS_SLOT,
	CLASS_ICON,
	ATTR_VARIATION,
	ATTR_ICON,
	ATTR_ICON_POS,
	ICON_PROP_POS,
	ATTR_ICON_MARGIN,
	DEFAULT_CSS_VAR_ICON_MARGIN,
	CSS_PROPERTY_ICON_MARGIN,
	ATTR_HIDE,
	STATE_ROUNDED,
	STATE_LOADING,
	STATE_DISABLED,
	STATE_ICON,
	STATE_WITHOUT_ICON,
	ATTR_ROUNDED,
	ATTR_LOADING,
	ATTR_DISABLED,
	EVENT_LOADING_STATE_CHANGED,
	DEFAULT_BUTTON_OPTIONS,
	BUTTON_ATTRIBUTE_MAP,
	BnumButtonUi,
} from './internal';
import BnumElementInternal from 'components/bnum-element-states';
import { Listener } from 'core/decorators/listener';
import { ErrorPath, RiskyPath } from '@rotomeca/rop';
import Log from 'core/utils/Log';
import { UI } from 'core/decorators/ui';

//#region External Constants
// Constantes pour les tags des différents types de boutons
/**
 * Icône de chargement utilisée dans le bouton.
 */
const ICON_LOADER = 'progress_activity';
//#endregion External Constants

//#region Template
const TEMPLATE = (
	<div class={CLASS_WRAPPER} part="container">
		<span class={CLASS_SLOT}>
			<slot></slot>
		</span>
		<HTMLBnumIcon hidden="true" class={CLASS_ICON}></HTMLBnumIcon>
	</div>
);
//#endregion Template

//#region Documentation
/**
 * Composant bouton principal de la bibliothèque Bnum.
 * Gère les variations, l'icône, l'état de chargement, etc.
 *
 * @category Buttons
 *
 * @structure Bouton primaire
 * <bnum-button data-variation="primary">Texte du bouton</bnum-button>
 *
 * @structure Bouton secondaire
 * <bnum-button data-variation="secondary">Texte du bouton</bnum-button>
 *
 * @structure Bouton danger
 * <bnum-button data-variation="danger">Texte du bouton</bnum-button>
 *
 * @structure Bouton avec icône
 * <bnum-button data-icon="home">Texte du bouton</bnum-button>
 *
 * @structure Bouton avec une icône à gauche
 * <bnum-button data-icon="home" data-icon-pos="left">Texte du bouton</bnum-button>
 *
 * @structure Bouton en état de chargement
 * <bnum-button loading>Texte du bouton</bnum-button>
 *
 * @structure Bouton arrondi
 * <bnum-button rounded>Texte du bouton</bnum-button>
 *
 * @structure Bouton cachant le texte sur les petits layouts
 * <bnum-button data-hide="small" data-icon="menu">Menu</bnum-button>
 *
 * @slot (default) - Contenu du bouton (texte, HTML, etc.)
 *
 * @state loading - Actif si le bouton est en état de chargement
 * @state rounded - Actif si le bouton est arrondi
 * @state disabled - Actif si le bouton est désactivé
 * @state icon - Actif si le bouton contient une icône
 * @state without-icon - Actif si le bouton ne contient pas d'icône
 * @state icon-pos-left - Actif si l'icône est positionnée à gauche
 * @state icon-pos-right - Actif si l'icône est positionnée à droite
 * @state hide-text-on-small - Actif si le texte est caché sur les petits layouts
 * @state hide-text-on-touch - Actif si le texte est caché sur les layouts tactiles
 * @state primary - Actif si le bouton est de type primaire
 * @state secondary - Actif si le bouton est de type secondaire
 * @state tertiary - Actif si le bouton est de type tertiaire
 * @state danger - Actif si le bouton est de type danger
 *
 * @attr {boolean | undefined} (optional) rounded - Rend le bouton arrondi
 * @attr {boolean | undefined} (optional) loading - Met le bouton en état de chargement et le désactive
 * @attr {boolean | undefined} (optional) disabled - Désactive le bouton
 * @attr {Optional<ButtonVariation>} (optional) (default: ButtonVariation.PRIMARY) data-variation - Variation du bouton (primary, secondary, etc.)
 * @attr {string | undefined} (optional) data-icon - Icône affichée dans le bouton
 * @attr {IconPosition | undefined} (optional) (default: IconPosition.RIGHT) data-icon-pos - Position de l'icône (gauche ou droite)
 * @attr {string | undefined} (optional) (default: var（--custom-bnum-button-icon-margin, 10px）) data-icon-margin - Marge de l'icône (gauche, droite)
 * @attr {HideTextOnLayoutSize | undefined} (optional) data-hide - Taille de layout pour cacher le texte
 *
 * @event {ElementChangedEvent} custom:element-changed.variation - Événement déclenché lors du changement de variation du bouton.
 * @event {ElementChangedEvent} custom:element-changed.icon - Événement déclenché lors du changement d'icône.
 * @event {CustomEvent<{ type: string, newValue: boolean | string }>} custom:icon.prop.changed - Événement déclenché lors du changement de propriété de l'icône.
 * @event {CustomEvent<{ state: boolean }>} custom:loading - Événement déclenché lors du changement d'état de chargement.
 *
 * @cssvar {inline-block} --bnum-button-display - Définit le type d'affichage du bouton
 * @cssvar {6px 10px} --bnum-button-padding - Définit le padding interne du bouton
 * @cssvar {0} --bnum-button-border-radius - Définit l'arrondi des coins du bouton
 * @cssvar {pointer} --bnum-button-cursor - Définit le curseur de la souris au survol du bouton
 * @cssvar {5px} --bnum-button-rounded-border-radius - Arrondi des coins pour le bouton arrondi
 * @cssvar {7.5px} --bnum-button-without-icon-padding-top - Padding top si le bouton n'a pas d'icône
 * @cssvar {7.5px} --bnum-button-without-icon-padding-bottom - Padding bottom si le bouton n'a pas d'icône
 * @cssvar {var(--bnum-color-primary)} --bnum-button-primary-background-color - Couleur de fond du bouton (état primaire)
 * @cssvar {var(--bnum-text-on-primary)} --bnum-button-primary-text-color - Couleur du texte du bouton (état primaire)
 * @cssvar {solid thin var(--bnum-button-primary-border-color)} --bnum-button-primary-border - Bordure du bouton (état primaire)
 * @cssvar {var(--bnum-color-primary)} --bnum-button-primary-border-color - Couleur de la bordure (état primaire)
 * @cssvar {var(--bnum-color-primary-hover)} --bnum-button-primary-hover-background-color - Couleur de fond au survol (état primaire)
 * @cssvar {var(--bnum-text-on-primary-hover)} --bnum-button-primary-hover-text-color - Couleur du texte au survol (état primaire)
 * @cssvar {solid thin var(--bnum-button-primary-hover-border-color)} --bnum-button-primary-hover-border - Bordure au survol (état primaire)
 * @cssvar {var(--bnum-color-primary-hover)} --bnum-button-primary-hover-border-color - Couleur de la bordure au survol (état primaire)
 * @cssvar {var(--bnum-color-primary-active)} --bnum-button-primary-active-background-color - Couleur de fond lors du clic (état primaire)
 * @cssvar {var(--bnum-text-on-primary-active)} --bnum-button-primary-active-text-color - Couleur du texte lors du clic (état primaire)
 * @cssvar {solid thin var(--bnum-button-primary-active-border-color)} --bnum-button-primary-active-border - Bordure lors du clic (état primaire)
 * @cssvar {var(--bnum-color-primary-active)} --bnum-button-primary-active-border-color - Couleur de la bordure lors du clic (état primaire)
 * @cssvar {var(--bnum-color-secondary)} --bnum-button-secondary-background-color - Couleur de fond (état secondaire)
 * @cssvar {var(--bnum-text-on-secondary)} --bnum-button-secondary-text-color - Couleur du texte (état secondaire)
 * @cssvar {solid thin var(--bnum-button-secondary-border-color)} --bnum-button-secondary-border - Bordure (état secondaire)
 * @cssvar {var(--bnum-color-primary)} --bnum-button-secondary-border-color - Couleur de la bordure (état secondaire)
 * @cssvar {var(--bnum-color-secondary-hover)} --bnum-button-secondary-hover-background-color - Couleur de fond au survol (état secondaire)
 * @cssvar {var(--bnum-text-on-secondary-hover)} --bnum-button-secondary-hover-text-color - Couleur du texte au survol (état secondaire)
 * @cssvar {solid thin var(--bnum-button-secondary-hover-border-color)} --bnum-button-secondary-hover-border - Bordure au survol (état secondaire)
 * @cssvar {var(--bnum-color-primary)} --bnum-button-secondary-hover-border-color - Couleur de la bordure au survol (état secondaire)
 * @cssvar {var(--bnum-color-secondary-active)} --bnum-button-secondary-active-background-color - Couleur de fond lors du clic (état secondaire)
 * @cssvar {var(--bnum-text-on-secondary-active)} --bnum-button-secondary-active-text-color - Couleur du texte lors du clic (état secondaire)
 * @cssvar {solid thin var(--bnum-button-secondary-active-border-color)} --bnum-button-secondary-active-border - Bordure lors du clic (état secondaire)
 * @cssvar {var(--bnum-color-primary)} --bnum-button-secondary-active-border-color - Couleur de la bordure lors du clic (état secondaire)
 * @cssvar {var(--bnum-color-danger)} --bnum-button-danger-background-color - Couleur de fond (état danger)
 * @cssvar {var(--bnum-text-on-danger)} --bnum-button-danger-text-color - Couleur du texte (état danger)
 * @cssvar {solid thin var(--bnum-button-danger-border-color)} --bnum-button-danger-border - Bordure (état danger)
 * @cssvar {var(--bnum-color-danger)} --bnum-button-danger-border-color - Couleur de la bordure (état danger)
 * @cssvar {var(--bnum-color-danger-hover)} --bnum-button-danger-hover-background-color - Couleur de fond au survol (état danger)
 * @cssvar {var(--bnum-text-on-danger-hover)} --bnum-button-danger-hover-text-color - Couleur du texte au survol (état danger)
 * @cssvar {solid thin var(--bnum-button-danger-hover-border-color)} --bnum-button-danger-hover-border - Bordure au survol (état danger)
 * @cssvar {var(--bnum-color-danger-hover)} --bnum-button-danger-hover-border-color - Couleur de la bordure au survol (état danger)
 * @cssvar {var(--bnum-color-danger-active)} --bnum-button-danger-active-background-color - Couleur de fond lors du clic (état danger)
 * @cssvar {var(--bnum-text-on-danger-active)} --bnum-button-danger-active-text-color - Couleur du texte lors du clic (état danger)
 * @cssvar {solid thin var(--bnum-button-danger-active-border-color)} --bnum-button-danger-active-border - Bordure lors du clic (état danger)
 * @cssvar {var(--bnum-color-danger-active)} --bnum-button-danger-active-border-color - Couleur de la bordure lors du clic (état danger)
 * @cssvar {0.6} --bnum-button-disabled-opacity - Opacité du bouton désactivé
 * @cssvar {none} --bnum-button-disabled-pointer-events - Gestion des événements souris pour le bouton désactivé
 * @cssvar {flex} --bnum-button-wrapper-display - Type d'affichage du wrapper interne
 * @cssvar {center} --bnum-button-wrapper-align-items - Alignement vertical du contenu du wrapper
 * @cssvar {flex} --bnum-button-icon-display - Type d'affichage de l'icône
 * @cssvar {flex} --bnum-button-loader-display - Type d'affichage du loader
 * @cssvar {0.75s} --bnum-button-spin-duration - Durée de l'animation de spin
 * @cssvar {linear} --bnum-button-spin-timing - Fonction de timing de l'animation de spin
 * @cssvar {infinite} --bnum-button-spin-iteration - Nombre d'itérations de l'animation de spin
 * @cssvar {-3px} --bnum-button-margin-bottom-text-correction - Correction basse du texte
 */
@Define({
	tag: TAG_BUTTON,
	template: TEMPLATE,
	styles: style,
})
export class HTMLBnumButton extends BnumElementInternal {
	//#endregion Component Definition

	//#region Private fields
	#_renderScheduler: Nullable<Scheduler<void>> = null;
	#_lastClick: Nullable<string> = null;
	//#endregion Private fields
	//#region Getter/setter
	/**
	 * Références UI du composant.
	 */
	@UI({
		wrapper: `.${CLASS_WRAPPER}`,
		icon: `.${CLASS_ICON}`,
	})
	accessor #_ui!: BnumButtonUi;

	/**
	 * Événement déclenché lors du changement d'état de chargement.
	 */
	@Listener(OnLoadingStateChangeInitializer, { lazy: false })
	accessor onloadingstatechange!: LoadingStateChangedEvent;
	/**
	 * Événement déclenché lors du changement d'icône.
	 */
	@Listener(OnIconChangeInitializer, { lazy: false })
	accessor oniconchange!: IconChangedEvent;
	/**
	 * Événement déclenché lors du changement de propriété de l'icône.
	 */
	@Listener(OnIconPropChangeInitializer, { lazy: false })
	accessor oniconpropchange!: IconPropChangedEvent;
	/**
	 * Événement déclenché lors du changement de variation du bouton.
	 */
	@Listener(OnVariationChangeInitializer, { lazy: false })
	accessor onvariationchange!: VariationChangedEvent;

	/** Événement déclenché lors du clic sur le bouton. */
	@Listener(OnClickInitializer)
	accessor linkedClickEvent!: LinkedClickEvent;

	/**
	 * Variation du bouton (primary, secondary, etc.).
	 */
	get variation(): ButtonVariation {
		return (
			(this.data(ATTR_VARIATION) as ButtonVariation) || ButtonVariation.PRIMARY
		);
	}
	set variation(value: ButtonVariation) {
		if (Object.values(ButtonVariation).includes(value)) {
			const fromAttribute = false;
			this.data(ATTR_VARIATION, value, fromAttribute);

			if (this.alreadyLoaded) {
				this.onvariationchange.call(value, this.variation);
				this.#_requestUpdateDOM();
			}
		}
	}

	/**
	 * Icône affichée dans le bouton.
	 */
	get icon(): Nullable<string> {
		return (this.data(ATTR_ICON) as string) || null;
	}
	set icon(value: Nullable<string>) {
		if (this.alreadyLoaded)
			this.oniconchange.call(value || EMPTY_STRING, this.icon || EMPTY_STRING);

		if (typeof value === 'string' && /^[\w-]+$/.test(value)) {
			const fromAttribute = false;
			this.data(ATTR_ICON, value, fromAttribute);
		} else {
			this.data(ATTR_ICON, null);
		}

		if (this.alreadyLoaded) this.#_requestUpdateDOM();
	}

	/**
	 * Position de l'icône (gauche ou droite).
	 */
	get iconPos(): IconPosition {
		return (this.data(ATTR_ICON_POS) as IconPosition) || IconPosition.RIGHT;
	}
	set iconPos(value: IconPosition) {
		if (this.alreadyLoaded) this.oniconpropchange.call(ICON_PROP_POS, value);

		if (Object.values(IconPosition).includes(value)) {
			const fromAttribute = false;
			this.data(ATTR_ICON_POS, value, fromAttribute);
		}

		if (this.alreadyLoaded) this.#_requestUpdateDOM();
	}

	/**
	 * Marge appliquée à l'icône.
	 */
	get iconMargin(): string {
		return (
			(this.data(ATTR_ICON_MARGIN) as string) || DEFAULT_CSS_VAR_ICON_MARGIN
		);
	}
	set iconMargin(value: string | null) {
		if (this.alreadyLoaded)
			this.oniconpropchange.call('margin', value || EMPTY_STRING);

		if (typeof value === 'string' && REG_XSS_SAFE.test(value)) {
			const fromAttribute = false;
			this.data(ATTR_ICON_MARGIN, value, fromAttribute);
			this.style.setProperty(CSS_PROPERTY_ICON_MARGIN, value);
		} else if (value === null) {
			this.data(ATTR_ICON_MARGIN, value);
			this.style.removeProperty(CSS_PROPERTY_ICON_MARGIN);
		}
	}

	/**
	 * Taille de layout sur laquelle le texte doit être caché.
	 */
	get hideTextOnLayoutSize(): Nullable<HideTextOnLayoutSize> {
		const data = this.data<HideTextOnLayoutSize>(ATTR_HIDE);

		if (
			[...Object.values(HideTextOnLayoutSize), null, undefined].includes(data)
		)
			return data as HideTextOnLayoutSize;
		return null;
	}
	//#endregion Getter/setter
	//#region Lifecycle
	/**
	 * Constructeur du bouton Bnum.
	 */
	constructor() {
		super();
	}

	/**
	 * Construit le DOM du composant bouton.
	 * @param container - Le conteneur du Shadow DOM.
	 */
	protected _p_buildDOM(): void {
		if (this.data(ATTR_ICON_MARGIN)) {
			this.style.setProperty(
				CSS_PROPERTY_ICON_MARGIN,
				this.data(ATTR_ICON_MARGIN),
			);
		}

		this.#_updateDOM();

		HTMLBnumButton.ToButton(this);
	}

	protected _p_update(): void {
		if (!this.#_ui.wrapper) return;

		this.#_updateDOM();
	}
	//#endregion Lifecycle

	//#region Private methods
	/**
	 * Demande une mise à jour du DOM du bouton.
	 */
	#_requestUpdateDOM(): void {
		this.#_renderScheduler ??= new Scheduler<void>(() => {
			this.#_updateDOM();
		});
		this.#_renderScheduler.schedule();
	}

	/**
	 * Met à jour le DOM du bouton (icône, états, etc.).
	 * @private
	 */
	#_updateDOM(): void {
		const isLoading = this.#_isLoading();
		const isDisabled = this.#_isDisabled();

		// Reset des états
		this._p_clearStates();

		// États globaux
		this._p_addState(this.variation);
		this._p_addState(this.variation);
		if (this.#_isRounded()) this._p_addState(STATE_ROUNDED);
		if (isLoading) this._p_addState(STATE_LOADING);
		if (isDisabled || isLoading) this._p_addState(STATE_DISABLED);

		// Gestion de l'icône
		const effectiveIcon = isLoading ? ICON_LOADER : this.icon;

		if (effectiveIcon) {
			this._p_addState(STATE_ICON);

			// L'état CSS "icon-pos-left" déclenchera le "flex-direction: row-reverse"
			this._p_addState(`icon-pos-${this.iconPos}`);

			if (this.hideTextOnLayoutSize) {
				this._p_addState(`hide-text-on-${this.hideTextOnLayoutSize}`);
			}

			// Mise à jour du composant icône enfant
			if (this.#_ui.icon.icon !== effectiveIcon)
				this.#_ui.icon.icon = effectiveIcon;

			this.#_ui.icon.hidden = false;
		} else {
			this._p_addState(STATE_WITHOUT_ICON);
			this.#_ui.icon.hidden = true;
		}

		// Accessibilité (Internals gère aria-disabled, mais tabindex doit être géré ici)
		this._p_internal.ariaDisabled = String(isDisabled || isLoading);
		this.tabIndex = isDisabled || isLoading ? -1 : 0;

		this.#_setLinkedEvent();
	}

	/**
	 * Indique si le bouton est arrondi.
	 * @private
	 */
	#_isRounded(): boolean {
		return this.#_is(ATTR_ROUNDED);
	}
	/**
	 * Indique si le bouton est en état de chargement.
	 * @private
	 */
	#_isLoading(): boolean {
		return this.#_is(ATTR_LOADING);
	}
	/**
	 * Indique si le bouton est désactivé.
	 * @private
	 */
	#_isDisabled(): boolean {
		return this.#_is(ATTR_DISABLED);
	}

	/**
	 * Vérifie la présence d'un attribut et sa valeur.
	 * @private
	 * @param attr Nom de l'attribut à vérifier
	 * @returns true si l'attribut est présent et sa valeur est valide
	 */
	#_is(attr: string): boolean {
		return (
			this.hasAttribute(attr) &&
			!['false', false].includes(this.getAttribute(attr) as any)
		);
	}

	/**
	 * Ajoute l'événement lié au clic d'un autre élément si l'attribut est présent.
	 * @returns Cette instance du bouton
	 */
	#_setLinkedEvent(): this {
		if (this.hasAttribute('click')) {
			const click = this.getAttribute('click');

			if (click !== this.#_lastClick) {
				if (this.linkedClickEvent.has('click'))
					this.linkedClickEvent.remove('click');

				if (click && REG_XSS_SAFE.test(click)) {
					this.#_lastClick = click;
					this.linkedClickEvent.add('click', this.#_onLinkedClick, click);
				}
			}
		}

		return this;
	}
	//#endregion Private methods

	//#region Event handlers
	/**
	 * Action lors du clic lié à un autre élément.
	 * @param click Identifiant ou sélecteur CSS de l'élément à cliquer
	 */
	@Autobind
	@ErrorPath((e: Error) => {
		Log.error(`${TAG_BUTTON}/_onLinkedClick`, e.message, e);
	})
	@RiskyPath()
	#_onLinkedClick(click: string): void {
		// Si c'est un id unique
		const elementToClick = document.getElementById(click!);

		if (elementToClick) elementToClick.click();
		else {
			// Sinon on part du principe que c'est un sélecteur CSS
			const elements = document.querySelector(click!);
			if (elements) (elements as HTMLElement).click();
			else throw new Error("L'attribut 'click' ne référence aucun élément.");
		}
	}

	/**
	 * Gestion du changement d'état de chargement.
	 * @param state Nouvel état de chargement
	 * @returns Détail de l'événement
	 */
	@Fire(EVENT_LOADING_STATE_CHANGED)
	#_onLoadingChange(state: boolean): { state: boolean } {
		return { state };
	}

	@Autobind
	protected _p_onLoadingChange(state: boolean): { state: boolean } {
		return this.#_onLoadingChange(state);
	}
	//#endregion Event handlers

	//#region Public methods
	/**
	 * Met le bouton en état de chargement.
	 * @returns L'instance du bouton
	 */
	@SetAttr(ATTR_LOADING, true)
	setLoading(): this {
		return this;
	}
	/**
	 * Arrête l'état de chargement du bouton.
	 * @returns L'instance du bouton
	 */
	stopLoading(): this {
		this.removeAttribute(ATTR_LOADING);
		return this;
	}

	/**
	 * Bascule l'état de chargement du bouton.
	 * @returns L'instance du bouton
	 */
	toggleLoading(): this {
		if (this.#_isLoading()) {
			this.stopLoading();
		} else {
			this.setLoading();
		}
		return this;
	}
	//#endregion Public methods
	//#region Static methods
	/**
	 * Retourne la liste des attributs observés par le composant.
	 */
	static _p_observedAttributes(): string[] {
		return [ATTR_ROUNDED, ATTR_LOADING, ATTR_DISABLED, 'click'];
	}

	/**
	 * Transforme un élément en bouton accessible (role, tabindex, etc.).
	 * @static
	 * @param element Élément HTML à transformer
	 * @returns L'élément modifié
	 */
	static ToButton<T extends HTMLElement>(element: T): T {
		if (!element.onkeydown) {
			element.onkeydown = (e: KeyboardEvent) => {
				if (
					(e.key === ' ' || e.key === 'Enter') &&
					e.target instanceof HTMLElement
				) {
					e.target.click();
				}
			};
			element.setAttribute('data-set-event', 'onkeydown');
		}

		if (
			!element.hasAttribute('role') ||
			element.getAttribute('role') !== 'button'
		)
			element.setAttribute('role', 'button');
		if (!element.hasAttribute('tabindex'))
			element.setAttribute('tabindex', '0');
		return element;
	}

	/**
	 * Crée un bouton Bnum avec les options spécifiées.
	 * @static
	 * @param buttonClass Classe du bouton à instancier
	 * @param options Options de configuration du bouton
	 * @returns Instance du bouton créé
	 */
	static _p_Create<T extends HTMLBnumButton>(
		buttonClass: { TAG: string },
		options?: Optional<BnumButtonCreateOptions>,
	): T {
		const config = { ...DEFAULT_BUTTON_OPTIONS, ...options };

		const node = document.createElement(buttonClass.TAG) as unknown as T;
		node.textContent = config.text ?? EMPTY_STRING;

		const finalMargin = config.iconMargin === 0 ? '0px' : config.iconMargin;

		for (const { prop, attr, isBool } of BUTTON_ATTRIBUTE_MAP) {
			const val =
				prop === 'iconMargin'
					? finalMargin
					: config[prop as keyof typeof config];

			if (val === null || val === undefined) continue;

			if (isBool && val === true) node.setAttribute(attr, 'true');
			else if (!isBool && val !== EMPTY_STRING)
				node.setAttribute(attr, String(val));
		}

		return node;
	}

	/**
	 * Crée un bouton Bnum standard.
	 * @static
	 * @param options Options de configuration du bouton
	 * @returns Instance du bouton créé
	 */
	static Create(options?: Optional<BnumButtonCreateOptions>): HTMLBnumButton {
		return this._p_Create(this, options);
	}

	/**
	 * Crée un bouton Bnum ne contenant qu'une icône.
	 * @static
	 * @param icon Nom de l'icône à afficher
	 * @param options Options de configuration du bouton
	 * @returns Instance du bouton créé
	 */
	static CreateOnlyIcon(
		icon: string,
		{
			variation = ButtonVariation.PRIMARY,
			rounded = false,
			loading = false,
		}: {
			variation?: ButtonVariation | null;
			rounded?: boolean;
			loading?: boolean;
		} = {},
	): HTMLBnumButton {
		return this.Create({
			icon,
			variation,
			rounded,
			loading,
			iconMargin: '0px',
		});
	}
	//#endregion Static methods
}
